import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Search,
  X,
  Gauge,
  Link2,
  Users,
  Layers,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  Inbox,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

/* ------------------------------------------------------------------ */
/*  Styles partagés                                                    */
/* ------------------------------------------------------------------ */

const inputClass =
  'w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white placeholder-gray-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all';

const labelClass = 'mb-1.5 block text-sm font-medium text-gray-700';

const primaryBtn =
  'inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-blue-600 text-white ' +
  'rounded-xl shadow-sm hover:bg-blue-700 active:scale-[0.98] transition-all ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 disabled:active:scale-100';

const secondaryBtn =
  'inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-white text-gray-700 ' +
  'border border-gray-200 rounded-xl hover:bg-gray-50 active:scale-[0.98] transition-all ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30';

const keyframes = `
@keyframes ind-modal-in { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: none; } }
@keyframes ind-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes ind-toast-in { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: none; } }
.ind-modal-in { animation: ind-modal-in .18s ease-out; }
.ind-fade-in { animation: ind-fade-in .15s ease-out; }
.ind-toast-in { animation: ind-toast-in .2s ease-out; }
@media (prefers-reduced-motion: reduce) {
  .ind-modal-in, .ind-fade-in, .ind-toast-in { animation: none; }
}
`;

/* ------------------------------------------------------------------ */
/*  Petits composants                                                  */
/* ------------------------------------------------------------------ */

const StatCard = ({ icon: Icon, label, value, hint }) => (
  <div className="flex items-center gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-2xl font-semibold leading-tight text-gray-900">{value}</div>
      <div className="truncate text-sm text-gray-500">{label}</div>
      {hint && <div className="truncate text-xs text-gray-400">{hint}</div>}
    </div>
  </div>
);

const SkeletonRows = ({ cols = 4, rows = 5 }) => (
  <div className="divide-y divide-gray-100">
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="flex items-center gap-6 px-6 py-4">
        {Array.from({ length: cols }).map((__, c) => (
          <div
            key={c}
            className="h-4 animate-pulse rounded bg-gray-100"
            style={{ width: c === 0 ? '8%' : `${60 / cols + 10}%` }}
          />
        ))}
      </div>
    ))}
  </div>
);

const EmptyState = ({ icon: Icon, title, text, action }) => (
  <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-500">
      <Icon className="h-7 w-7" />
    </div>
    <h4 className="text-base font-semibold text-gray-800">{title}</h4>
    <p className="mt-1 max-w-sm text-sm text-gray-500">{text}</p>
    {action && <div className="mt-5">{action}</div>}
  </div>
);

const getPages = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('…');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push('…');
  pages.push(total);
  return pages;
};

const Pagination = ({ current, total, from, to, count, onChange }) => {
  if (total <= 1) return null;
  return (
    <div className="flex flex-col gap-3 border-t border-gray-100 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm text-gray-500">
        Affichage de <span className="font-medium text-gray-700">{from}</span> à{' '}
        <span className="font-medium text-gray-700">{to}</span> sur{' '}
        <span className="font-medium text-gray-700">{count}</span>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(Math.max(1, current - 1))}
          disabled={current === 1}
          aria-label="Page précédente"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        {getPages(current, total).map((page, i) =>
          page === '…' ? (
            <span key={`dots-${i}`} className="px-2 text-gray-400">
              …
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onChange(page)}
              aria-current={current === page ? 'page' : undefined}
              className={`h-9 min-w-[2.25rem] rounded-lg border px-3 text-sm font-medium transition-colors ${
                current === page
                  ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {page}
            </button>
          )
        )}
        <button
          onClick={() => onChange(Math.min(total, current + 1))}
          disabled={current === total}
          aria-label="Page suivante"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const Modal = ({ title, subtitle, onClose, maxWidth = 'max-w-md', children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="ind-fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={`ind-modal-in w-full ${maxWidth} overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-2xl`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-white px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-blue-900">{title}</h3>
            {subtitle && <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-white hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Toggle = ({ checked, onChange, label, description }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className="flex w-full items-center justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3 text-left transition-colors hover:bg-gray-50"
  >
    <span>
      <span className="block text-sm font-medium text-gray-700">{label}</span>
      {description && <span className="block text-xs text-gray-500">{description}</span>}
    </span>
    <span
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-blue-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </span>
  </button>
);

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const Indicateurs = () => {
  const { user } = useAuth();
  const { filialeId, isConsolidated } = useFiliale();
  const [indicateurs, setIndicateurs] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAssocModal, setShowAssocModal] = useState(false);
  const [editingIndicateur, setEditingIndicateur] = useState(null);
  const [searchIndicateur, setSearchIndicateur] = useState('');
  const [searchAssociation, setSearchAssociation] = useState('');
  const [currentPageIndicateur, setCurrentPageIndicateur] = useState(1);
  const [currentPageAssociation, setCurrentPageAssociation] = useState(1);
  const [activeTab, setActiveTab] = useState('indicateurs');
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const itemsPerPage = 10;
  const [formData, setFormData] = useState({
    Libelle_indicateur: '',
    editable: true
  });
  const [assocFormData, setAssocFormData] = useState({
    idindicateur: '',
    idtype_personnel: '',
    coeficient: 1.0,
    plafond: ''
  });
  const [typesPersonnel, setTypesPersonnel] = useState([]);

  const needsFiliale = isConsolidated || !filialeId || filialeId === 'all';

  /* ---------- Notifications (remplace alert) ---------- */
  const notify = useCallback((type, message) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, type, message }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3800);
  }, []);

  /* ---------- Filtrage ---------- */
  const filteredIndicateurs = indicateurs.filter(
    (ind) =>
      (ind.Libelle_indicateur || '').toLowerCase().includes(searchIndicateur.toLowerCase()) ||
      (ind.nom_variable || '').toLowerCase().includes(searchIndicateur.toLowerCase())
  );

  const filteredAssociations = associations.filter(
    (assoc) =>
      (assoc.Libelle_indicateur || '').toLowerCase().includes(searchAssociation.toLowerCase()) ||
      (assoc.type_personnel_libelle || '').toLowerCase().includes(searchAssociation.toLowerCase())
  );

  /* ---------- Pagination ---------- */
  const indexOfLastIndicateur = currentPageIndicateur * itemsPerPage;
  const indexOfFirstIndicateur = indexOfLastIndicateur - itemsPerPage;
  const currentIndicateurs = filteredIndicateurs.slice(indexOfFirstIndicateur, indexOfLastIndicateur);
  const totalPagesIndicateur = Math.ceil(filteredIndicateurs.length / itemsPerPage);

  const indexOfLastAssociation = currentPageAssociation * itemsPerPage;
  const indexOfFirstAssociation = indexOfLastAssociation - itemsPerPage;
  const currentAssociations = filteredAssociations.slice(indexOfFirstAssociation, indexOfLastAssociation);
  const totalPagesAssociation = Math.ceil(filteredAssociations.length / itemsPerPage);

  useEffect(() => {
    setCurrentPageIndicateur(1);
  }, [searchIndicateur]);

  useEffect(() => {
    setCurrentPageAssociation(1);
  }, [searchAssociation]);

  /* ---------- Chargement des données ---------- */
  const fetchIndicateurs = async () => {
    try {
      setLoading(true);
      let endpoint = '/api/indicateurs';
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        endpoint += `?filiale_id=${filialeId}`;
      }
      const response = await http.get(endpoint);
      setIndicateurs(response.data || []);
    } catch (error) {
      console.error('Erreur récupération indicateurs:', error);
      setIndicateurs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssociations = async () => {
    try {
      let endpoint = '/api/indicateurs/associations';
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        endpoint += `?filiale_id=${filialeId}`;
      }
      const response = await http.get(endpoint);
      setAssociations(response.data || []);
    } catch (error) {
      console.error('Erreur récupération associations:', error);
      setAssociations([]);
    }
  };

  const fetchTypesPersonnel = async () => {
    try {
      const [typesRes, personnelRes] = await Promise.all([
        http.get('/api/types-personnel').catch(() => ({ data: [] })),
        http.get('/api/personnel').catch(() => ({ data: [] }))
      ]);

      const allTypes = Array.isArray(typesRes.data) ? typesRes.data : [];
      const allPersonnel = Array.isArray(personnelRes.data?.data) ? personnelRes.data.data : [];

      // Si contexte filiale spécifique, filtrer les types qui ont du personnel dans cette filiale
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        const personnelInFiliale = allPersonnel.filter((p) => p.filiale_id === parseInt(filialeId));
        const typeIdsInFiliale = [...new Set(personnelInFiliale.map((p) => p.type_personnel_id))];
        const filteredTypes = allTypes.filter((t) => typeIdsInFiliale.includes(t.idtype_personnel));
        setTypesPersonnel(filteredTypes);
      } else {
        setTypesPersonnel(allTypes);
      }
    } catch (error) {
      console.error('Erreur récupération types personnel:', error);
      setTypesPersonnel([]);
    }
  };

  useEffect(() => {
    fetchIndicateurs();
    fetchAssociations();
    fetchTypesPersonnel();
  }, [filialeId, isConsolidated]);

  /* ---------- Indicateurs : actions ---------- */
  const handleCreate = () => {
    if (needsFiliale) {
      notify('warning', 'Sélectionnez une filiale spécifique pour créer un indicateur.');
      return;
    }
    setEditingIndicateur(null);
    setFormData({ Libelle_indicateur: '', editable: true, filiale_id: filialeId });
    setShowModal(true);
  };

  const handleEdit = (indicateur) => {
    setEditingIndicateur(indicateur);
    setFormData({
      Libelle_indicateur: indicateur.Libelle_indicateur,
      editable: indicateur.editable === 1
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setConfirmState({
      title: 'Supprimer cet indicateur ?',
      message: 'Les associations liées à cet indicateur seront aussi supprimées. Cette action est irréversible.',
      onConfirm: async () => {
        try {
          await http.delete(`/api/indicateurs/${id}`);
          notify('success', 'Indicateur supprimé avec succès');
          fetchIndicateurs();
          fetchAssociations();
        } catch (error) {
          console.error('Erreur suppression:', error);
          notify('error', 'Erreur lors de la suppression');
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingIndicateur) {
        await http.put(`/api/indicateurs/${editingIndicateur.idindicateur}`, formData);
        notify('success', 'Indicateur modifié avec succès');
      } else {
        await http.post('/api/indicateurs', formData);
        notify('success', 'Indicateur créé avec succès');
      }
      setShowModal(false);
      setEditingIndicateur(null);
      setFormData({ Libelle_indicateur: '', editable: true, filiale_id: filialeId });
      fetchIndicateurs();
    } catch (error) {
      console.error('Erreur soumission:', error);
      notify('error', 'Erreur lors de la soumission');
    }
  };

  /* ---------- Associations : actions ---------- */
  const handleCreateAssoc = () => {
    if (needsFiliale) {
      notify('warning', 'Sélectionnez une filiale spécifique pour créer une association.');
      return;
    }
    setAssocFormData({
      idindicateur: '',
      idtype_personnel: '',
      titre_id: '',
      coeficient: 1.0,
      plafond: '',
      filiale_id: filialeId
    });
    setShowAssocModal(true);
  };

  const handleDeleteAssoc = (id) => {
    setConfirmState({
      title: 'Supprimer cette association ?',
      message: "L'indicateur ne sera plus appliqué à ce type de personnel.",
      onConfirm: async () => {
        try {
          await http.delete(`/api/indicateurs/associations/${id}`);
          notify('success', 'Association supprimée avec succès');
          fetchAssociations();
        } catch (error) {
          console.error('Erreur suppression association:', error);
          notify('error', 'Erreur lors de la suppression');
        }
      }
    });
  };

  const handleSubmitAssoc = async (e) => {
    e.preventDefault();
    try {
      await http.post('/api/indicateurs/associations', assocFormData);
      notify('success', 'Association créée avec succès');
      setShowAssocModal(false);
      setAssocFormData({
        idindicateur: '',
        idtype_personnel: '',
        titre_id: '',
        coeficient: 1.0,
        plafond: '',
        filiale_id: filialeId
      });
      fetchAssociations();
    } catch (error) {
      console.error('Erreur soumission association:', error);
      notify('error', 'Erreur lors de la soumission');
    }
  };

  /* ---------- Stats ---------- */
  const nbEditables = indicateurs.filter((i) => i.editable).length;
  const nbTypesCouverts = new Set(associations.map((a) => a.type_personnel_libelle)).size;

  const isIndicateursTab = activeTab === 'indicateurs';

  /* ---------- Rendu ---------- */
  return (
    <div className="min-h-screen bg-gray-50">
      <style>{keyframes}</style>

      <BlueHeader
        title="Indicateurs de Performance"
        subtitle="Gérer les indicateurs de performance par type de personnel"
        actions={[
          {
            label: 'Nouvel indicateur',
            icon: Plus,
            onClick: handleCreate,
            primary: true,
            disabled: needsFiliale
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: () => {
              fetchIndicateurs();
              fetchAssociations();
            },
            primary: false
          }
        ]}
      />

      <div className="space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        {/* Bandeau contexte filiale */}
        {needsFiliale && (
          <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 text-sm text-blue-900">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <p>
              Vous consultez la vue consolidée. Choisissez une filiale dans le sélecteur pour créer ou modifier des
              indicateurs et des associations.
            </p>
          </div>
        )}

        {/* Statistiques */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard icon={Gauge} label="Indicateurs" value={loading ? '–' : indicateurs.length} hint={loading ? '' : `${nbEditables} éditable${nbEditables > 1 ? 's' : ''}`} />
          <StatCard icon={Link2} label="Associations" value={loading ? '–' : associations.length} />
          <StatCard icon={Users} label="Types de personnel couverts" value={loading ? '–' : nbTypesCouverts} />
        </div>

        {/* Carte principale avec onglets */}
        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          {/* Onglets + barre d'outils */}
          <div className="flex flex-col gap-4 border-b border-gray-100 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
            <div role="tablist" className="inline-flex rounded-xl bg-gray-100 p-1">
              {[
                { id: 'indicateurs', label: 'Indicateurs', icon: Layers, count: indicateurs.length },
                { id: 'associations', label: 'Associations', icon: Link2, count: associations.length }
              ].map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveTab(tab.id)}
                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                      active ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        active ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={isIndicateursTab ? 'Rechercher un indicateur ou une variable…' : 'Rechercher un indicateur ou un type…'}
                  value={isIndicateursTab ? searchIndicateur : searchAssociation}
                  onChange={(e) =>
                    isIndicateursTab ? setSearchIndicateur(e.target.value) : setSearchAssociation(e.target.value)
                  }
                  className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-9 text-sm placeholder-gray-400 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:w-72"
                />
                {(isIndicateursTab ? searchIndicateur : searchAssociation) && (
                  <button
                    onClick={() => (isIndicateursTab ? setSearchIndicateur('') : setSearchAssociation(''))}
                    aria-label="Effacer la recherche"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {!isIndicateursTab && (
                <button
                  onClick={handleCreateAssoc}
                  disabled={needsFiliale}
                  title={needsFiliale ? 'Sélectionnez une filiale pour créer une association' : undefined}
                  className={primaryBtn}
                >
                  <Plus className="h-4 w-4" />
                  Nouvelle association
                </button>
              )}
            </div>
          </div>

          {/* ---------- Onglet Indicateurs ---------- */}
          {isIndicateursTab && (
            <>
              {loading ? (
                <SkeletonRows cols={4} />
              ) : currentIndicateurs.length === 0 ? (
                <EmptyState
                  icon={searchIndicateur ? Search : Inbox}
                  title={searchIndicateur ? 'Aucun résultat' : 'Aucun indicateur trouvé'}
                  text={
                    searchIndicateur
                      ? `Aucun indicateur ne correspond à « ${searchIndicateur} ». Essayez un autre terme.`
                      : 'Créez votre premier indicateur pour commencer à suivre la performance.'
                  }
                  action={
                    searchIndicateur ? (
                      <button onClick={() => setSearchIndicateur('')} className={secondaryBtn}>
                        Effacer la recherche
                      </button>
                    ) : (
                      <button onClick={handleCreate} disabled={needsFiliale} className={primaryBtn}>
                        <Plus className="h-4 w-4" />
                        Nouvel indicateur
                      </button>
                    )
                  }
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        <th className="px-6 py-3">ID</th>
                        <th className="px-6 py-3">Libellé</th>
                        <th className="px-6 py-3">Variable</th>
                        <th className="px-6 py-3">Éditable</th>
                        <th className="px-6 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {currentIndicateurs.map((indicateur) => (
                        <tr key={indicateur.idindicateur} className="group transition-colors hover:bg-blue-50/40">
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-400">#{indicateur.idindicateur}</td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className="flex items-center gap-3">
                              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                                <Gauge className="h-4 w-4" />
                              </span>
                              <span className="text-sm font-medium text-gray-900">{indicateur.Libelle_indicateur}</span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <code className="rounded-md bg-gray-100 px-2 py-1 font-mono text-xs text-gray-700">
                              {indicateur.nom_variable}
                            </code>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <span
                              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                                indicateur.editable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              <span
                                className={`h-1.5 w-1.5 rounded-full ${
                                  indicateur.editable ? 'bg-green-500' : 'bg-gray-400'
                                }`}
                              />
                              {indicateur.editable ? 'Oui' : 'Non'}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className="flex justify-center gap-1">
                              <button
                                onClick={() => handleEdit(indicateur)}
                                className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-100"
                                title="Modifier"
                                aria-label="Modifier"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(indicateur.idindicateur)}
                                className="rounded-lg p-2 text-red-500 transition-colors hover:bg-red-50"
                                title="Supprimer"
                                aria-label="Supprimer"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <Pagination
                current={currentPageIndicateur}
                total={totalPagesIndicateur}
                from={indexOfFirstIndicateur + 1}
                to={Math.min(indexOfLastIndicateur, filteredIndicateurs.length)}
                count={filteredIndicateurs.length}
                onChange={setCurrentPageIndicateur}
              />
            </>
          )}

          {/* ---------- Onglet Associations ---------- */}
          {!isIndicateursTab && (
            <>
              {loading ? (
                <SkeletonRows cols={5} />
              ) : currentAssociations.length === 0 ? (
                <EmptyState
                  icon={searchAssociation ? Search : Link2}
                  title={searchAssociation ? 'Aucun résultat' : 'Aucune association trouvée'}
                  text={
                    searchAssociation
                      ? `Aucune association ne correspond à « ${searchAssociation} ».`
                      : 'Associez un indicateur à un type de personnel pour définir son coefficient et son plafond.'
                  }
                  action={
                    searchAssociation ? (
                      <button onClick={() => setSearchAssociation('')} className={secondaryBtn}>
                        Effacer la recherche
                      </button>
                    ) : (
                      <button onClick={handleCreateAssoc} disabled={needsFiliale} className={primaryBtn}>
                        <Plus className="h-4 w-4" />
                        Nouvelle association
                      </button>
                    )
                  }
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        <th className="px-6 py-3">ID</th>
                        <th className="px-6 py-3">Indicateur</th>
                        <th className="px-6 py-3">Type Personnel</th>
                        <th className="px-6 py-3">Coefficient</th>
                        <th className="px-6 py-3">Plafond</th>
                        <th className="px-6 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {currentAssociations.map((assoc) => (
                        <tr
                          key={assoc.idindicateur_has_type_personnel}
                          className="group transition-colors hover:bg-blue-50/40"
                        >
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-400">
                            #{assoc.idindicateur_has_type_personnel}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                            {assoc.Libelle_indicateur}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                              <Users className="h-3 w-3" />
                              {assoc.type_personnel_libelle}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <span className="rounded-md bg-gray-100 px-2 py-1 font-mono text-xs font-medium text-gray-800">
                              × {assoc.coeficient}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                            {assoc.plafond || <span className="text-gray-300">Aucun</span>}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className="flex justify-center">
                              <button
                                onClick={() => handleDeleteAssoc(assoc.idindicateur_has_type_personnel)}
                                className="rounded-lg p-2 text-red-500 transition-colors hover:bg-red-50"
                                title="Supprimer"
                                aria-label="Supprimer"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <Pagination
                current={currentPageAssociation}
                total={totalPagesAssociation}
                from={indexOfFirstAssociation + 1}
                to={Math.min(indexOfLastAssociation, filteredAssociations.length)}
                count={filteredAssociations.length}
                onChange={setCurrentPageAssociation}
              />
            </>
          )}
        </div>
      </div>

      {/* Modal création/modification indicateur */}
      {showModal && (
        <Modal
          title={`${editingIndicateur ? 'Modifier' : 'Créer'} Indicateur`}
          subtitle="Donnez un nom clair : il sera visible par les équipes."
          onClose={() => setShowModal(false)}
        >
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 px-6 py-5">
              <div>
                <label className={labelClass}>Libellé de l'indicateur</label>
                <input
                  type="text"
                  autoFocus
                  value={formData.Libelle_indicateur}
                  onChange={(e) => setFormData({ ...formData, Libelle_indicateur: e.target.value })}
                  className={inputClass}
                  placeholder="Ex: Taux de présence"
                  required
                />
              </div>
              <Toggle
                checked={!!formData.editable}
                onChange={(v) => setFormData({ ...formData, editable: v })}
                label="Éditable"
                description="Autorise la saisie manuelle de la valeur de cet indicateur"
              />
              {isConsolidated && (
                <div>
                  <label className={labelClass}>Filiale</label>
                  <select
                    value={formData.filiale_id || ''}
                    onChange={(e) => setFormData({ ...formData, filiale_id: e.target.value })}
                    className={inputClass}
                    required
                  >
                    <option value="">Sélectionner une filiale</option>
                    {/* À compléter avec les filiales */}
                  </select>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 border-t border-blue-100 bg-gray-50/60 px-6 py-4">
              <button type="button" onClick={() => setShowModal(false)} className={secondaryBtn}>
                Annuler
              </button>
              <button type="submit" className={primaryBtn}>
                {editingIndicateur ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal création association */}
      {showAssocModal && (
        <Modal
          title="Nouvelle Association"
          subtitle="Appliquez un indicateur à un type de personnel."
          maxWidth="max-w-lg"
          onClose={() => setShowAssocModal(false)}
        >
          <form onSubmit={handleSubmitAssoc}>
            <div className="space-y-4 px-6 py-5">
              <div>
                <label className={labelClass}>Indicateur</label>
                <select
                  value={assocFormData.idindicateur}
                  onChange={(e) => setAssocFormData({ ...assocFormData, idindicateur: e.target.value })}
                  className={inputClass}
                  required
                >
                  <option value="">Sélectionner un indicateur</option>
                  {indicateurs.map((ind) => (
                    <option key={ind.idindicateur} value={ind.idindicateur}>
                      {ind.Libelle_indicateur}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Type de personnel</label>
                <select
                  value={assocFormData.idtype_personnel}
                  onChange={(e) => setAssocFormData({ ...assocFormData, idtype_personnel: e.target.value })}
                  className={inputClass}
                  required
                >
                  <option value="">Sélectionner un type de personnel</option>
                  {typesPersonnel.map((tp) => (
                    <option key={tp.idtype_personnel} value={tp.idtype_personnel}>
                      {tp.libelle}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Coefficient</label>
                  <input
                    type="number"
                    step="0.01"
                    value={assocFormData.coeficient}
                    onChange={(e) => setAssocFormData({ ...assocFormData, coeficient: e.target.value })}
                    className={inputClass}
                    placeholder="1.00"
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Plafond <span className="font-normal text-gray-400">(optionnel)</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={assocFormData.plafond}
                    onChange={(e) => setAssocFormData({ ...assocFormData, plafond: e.target.value })}
                    className={inputClass}
                    placeholder="Aucun plafond"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-blue-100 bg-gray-50/60 px-6 py-4">
              <button type="button" onClick={() => setShowAssocModal(false)} className={secondaryBtn}>
                Annuler
              </button>
              <button type="submit" className={primaryBtn}>
                Créer
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Confirmation de suppression (remplace window.confirm) */}
      {confirmState && (
        <Modal title={confirmState.title} onClose={() => setConfirmState(null)}>
          <div className="flex items-start gap-3 px-6 py-5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <p className="pt-1 text-sm text-gray-600">{confirmState.message}</p>
          </div>
          <div className="flex justify-end gap-3 border-t border-blue-100 bg-gray-50/60 px-6 py-4">
            <button type="button" onClick={() => setConfirmState(null)} className={secondaryBtn}>
              Annuler
            </button>
            <button
              type="button"
              onClick={() => {
                const { onConfirm } = confirmState;
                setConfirmState(null);
                onConfirm();
              }}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-red-700 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40"
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </button>
          </div>
        </Modal>
      )}

      {/* Notifications */}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2" aria-live="polite">
        {toasts.map((t) => {
          const styles = {
            success: { box: 'border-green-200 bg-white', icon: CheckCircle2, color: 'text-green-600' },
            error: { box: 'border-red-200 bg-white', icon: AlertCircle, color: 'text-red-600' },
            warning: { box: 'border-amber-200 bg-white', icon: AlertTriangle, color: 'text-amber-600' }
          }[t.type];
          const Icon = styles.icon;
          return (
            <div
              key={t.id}
              className={`ind-toast-in pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg ${styles.box}`}
            >
              <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${styles.color}`} />
              <p className="flex-1 text-sm text-gray-800">{t.message}</p>
              <button
                onClick={() => setToasts((all) => all.filter((x) => x.id !== t.id))}
                aria-label="Fermer la notification"
                className="text-gray-400 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Indicateurs;